import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { AnchorProps } from "@headlessui/react/dist/internal/floating";
import classNames from "classnames";
import { ReactNode, useEffect, useRef, useState } from "react";

export const PopoverMenu = ({
  children,
  menuItems,
  customMenuItems = undefined,
  className = "",
  menuClassName = "",
  menuItemsClassName = "",
  position = "bottom",
  menuExactWidth = false,
  closeOnClick = false,
}: {
  children:
    | ReactNode
    | ((props: { open: boolean; close: () => void }) => ReactNode);
  customMenuItems?:
    | ReactNode
    | ((props: { open: boolean; close: () => void }) => ReactNode);
  menuItems?: {
    name: ReactNode;
    value?: string;
    onClick?: (e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  }[];
  className?: string;
  menuClassName?: string;
  menuItemsClassName?: string;
  position?: AnchorProps;
  menuExactWidth?: boolean;
  closeOnClick?: boolean;
}) => {
  const [width, setWidth] = useState(0);

  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (containerRef.current) {
      setWidth(containerRef.current.offsetWidth);
    }
  }, [containerRef?.current?.offsetWidth]);

  return (
    <Popover
      className={`relative inline-block text-left !outline-none focus-within:!outline-none focus:!outline-none ${className}`}
      data-open={true}
    >
      {({ open, close }) => {
        return (
          <>
            <div
              className="w-full outline-none focus-within:outline-none focus:outline-none"
              ref={containerRef}
            >
              <PopoverButton as={"div"} autoFocus={false}>
                {typeof children === "function"
                  ? children({ open, close })
                  : children}
              </PopoverButton>
            </div>

            <PopoverPanel
              className={classNames(
                `relative z-[9999] flex flex-col overflow-x-hidden overflow-y-scroll`,
                {
                  "rounded-md border bg-white": customMenuItems === undefined,
                  "w-full min-w-max": !menuExactWidth,
                },
                "[--anchor-gap:16px]",
                "outline-none focus-within:outline-none focus:outline-none",
                menuClassName,
              )}
              transition
              anchor={position}
              style={{
                width: width,
                minWidth: menuExactWidth ? `${width} !important` : undefined,
                maxWidth: menuExactWidth ? `${width} !important` : undefined,
              }}
              portal
            >
              {!!customMenuItems && (
                <>
                  {typeof customMenuItems === "function"
                    ? customMenuItems({ open, close })
                    : customMenuItems}
                </>
              )}

              {!customMenuItems && (
                <>
                  {menuItems?.map((item, index) => {
                    const key = `menu-item-${index}`;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={(e) => {
                          if (item.onClick) {
                            item.onClick(e);
                            if (closeOnClick) {
                              close();
                            }
                          }
                        }}
                        className={classNames(
                          "w-full border-b bg-inherit p-3 text-left text-sm font-medium text-primary transition-all duration-200 last:border-b-0 hover:bg-grey-50",
                          menuItemsClassName,
                        )}
                      >
                        {item.name}
                      </button>
                    );
                  })}
                </>
              )}
            </PopoverPanel>
          </>
        );
      }}
    </Popover>
  );
};
